import { useCallback } from 'react';
import { auditsAPI, type AuditDetailsInput } from '../../lib/api';
import { toast } from 'sonner';
import { apiError } from './useAuditActions';

/**
 * Edit / archive / restore / delete straight from an audit list row. Each
 * resolves null on success (and reloads the list), or the reason it failed —
 * the dialogs show that inline.
 *
 * @param onChanged - reload the list after a change
 * @returns updateDetails, archive, restore and remove for a given audit id
 */
export function useAuditRowActions(onChanged: () => void) {
  const run = useCallback(async (work: () => Promise<unknown>, success: string, failure: string) => {
    try {
      await work();
      toast.success(success);
      onChanged();
      return null;
    } catch (err: unknown) {
      return apiError(err, failure);
    }
  }, [onChanged]);

  return {
    updateDetails: (id: number, payload: AuditDetailsInput) =>
      run(() => auditsAPI.updateDetails(id, payload), 'Audit details saved', 'Could not save the audit details.'),
    archive: (id: number) => run(() => auditsAPI.archive(id), 'Audit archived', 'Could not archive the audit.'),
    restore: (id: number) => run(() => auditsAPI.unarchive(id), 'Audit restored', 'Could not restore the audit.'),
    remove: (id: number) => run(() => auditsAPI.remove(id), 'Audit deleted', 'Could not delete the audit.'),
  };
}
